import { Box, Grid, Stack, Typography } from "@mui/material";
import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import MobileStepper from "@mui/material/MobileStepper";
import Button from "@mui/material/Button";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import { useState, useEffect } from "react";

function DotsMobileStepper({ activeStep, setActiveStep, steps }) {
    const theme = useTheme();
    const [autoPlay, setAutoPlay] = useState(true); // Auto-play enabled by default

    const handleNext = () => {
        setActiveStep((prevActiveStep) => (prevActiveStep + 1) % steps);
        setAutoPlay(false); // Stop auto-play when manually changed
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => (prevActiveStep - 1 + steps) % steps);
        setAutoPlay(false); // Stop auto-play when manually changed
    };

    useEffect(() => {
        if (!autoPlay) return; // Stop auto-play when disabled

        const interval = setInterval(() => {
            setActiveStep((prevActiveStep) => (prevActiveStep + 1) % steps);
        }, 1000); // Change every 1 second

        return () => clearInterval(interval); // Cleanup on unmount
    }, [setActiveStep, steps, autoPlay]);

    return (
        <MobileStepper
            variant="dots"
            steps={steps}
            position="static"
            activeStep={activeStep}
            sx={{
                maxWidth: 400,
                flexGrow: 1,
                backgroundColor: "transparent",
                "& .MuiMobileStepper-dot": {
                    backgroundColor: "shade.light",
                },
                "& .MuiMobileStepper-dotActive": {
                    backgroundColor: "primary.main",
                },
            }}
            nextButton={
                <Button
                    size="small"
                    onClick={handleNext}
                    disabled={activeStep === steps - 1}
                    sx={{
                        color: activeStep === steps - 1 ? "shade.light" : "primary.main",
                        opacity: 1,
                        "&.Mui-disabled": { color: "shade.light" },
                    }}
                >
                    Next
                    {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                </Button>
            }
            backButton={
                <Button
                    size="small"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    sx={{
                        color: activeStep === 0 ? "shade.light" : "primary.main",
                        opacity: 1,
                        "&.Mui-disabled": { color: "shade.light" },
                    }}
                >
                    {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                    Back
                </Button>
            }
        />
    );
}


const Tech = styled(Typography)(({ theme }) => (
    {
        border:`1px solid ${theme.palette.primary.main}`,
        padding: `${theme.spacing(1)} ${ theme.spacing(3) }`,
        borderRadius:10
    }
))
const Feature = styled(Typography)(({ theme }) => (
    {
        fontStyle: "italic",
        color: theme.palette.secondary.main
    }
))

function AboutProject({ project }) {
    const [activeStep, setActiveStep] = React.useState(0);
    let theme = useTheme();

    if (!project || !project.details) {
        return null;
    }

    const currentImg = project.imgs && project.imgs.length > 0 ? project.imgs[activeStep] : project.imgSrc;
    const stepsCount = project.imgs && project.imgs.length > 0 ? project.imgs.length : 1;

    return (
        <Box
            sx={{
                height: "100%",
            }}
        >
            <Grid container 
                columnSpacing={5}               
                rowSpacing={3}
                sx={{
                    height: "100%",
                    alignItems: "center"
                }}
            >
                <Grid item xs={12} sm={12} md={6} 
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                   <Stack
                    direction="column"
                    alignItems="center"
                    spacing={2}
                    sx={{
                        width: "100%"
                    }}
                   >
                        <Box
                            component="img"
                            src={currentImg}
                            alt={project.projectName}
                            loading="lazy"
                            decoding="async"
                            sx={{
                                width: "100%",
                                maxHeight: { xs: "260px", sm: "360px", md: "420px" },
                                objectFit: "contain",
                                backgroundColor: "rgba(0, 0, 0, 0.3)",
                                border: `1px solid ${theme.palette.primary.main}`,
                                borderRadius: 3,
                                boxShadow: `0px 0px 15px ${theme.palette.primary.main}40`
                            }}
                        />
                        {stepsCount > 1 && (
                            <DotsMobileStepper activeStep={activeStep} setActiveStep={setActiveStep} steps={stepsCount} />
                        )}
                   </Stack>
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                        <Stack
                            direction="column"
                            justifyContent="center"
                            gap={2}
                            sx={{
                                height: '100%',
                            }}
                        >
                            <Typography variant="h4" color="text.primary" fontWeight="bold">
                                {project.projectName}
                            </Typography>
                            
                            <Typography variant="h6" color="shade.light">
                                {project.details.description}
                            </Typography>
                                <Stack
                                    direction="row"
                                    gap={1.5}
                                    sx={{
                                        flexWrap: "wrap"
                                    }}
                                >
                                    {
                                        project.details.features.map((val, idx) => <Feature key={idx}>• {val}</Feature>)
                                    }
                                </Stack>
                                <Stack
                                    direction="row"
                                    gap={1.5}
                                    sx={{
                                        flexWrap: "wrap"
                                    }}
                                >
                                    {
                                        project.details.technologies.map((val, idx) => <Tech key={idx}>{val}</Tech>)
                                    }
                                </Stack>
                        </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}

export default AboutProject;
